import { useCallback, useMemo } from "react";
import { FCX, Tabs, Tooltip } from "@datassential/platform-ui-lib";
import cn from "classnames";
import InputRadio from "@components/new/InputRadio/InputRadio";
import useLocalStorageState from "@hooks/useLocalStorageState";
import { CategoryProfileLaunches } from "@apiHooks/useCategoryProfileLaunches";
import CategoryProfileSubtitle from "@pages/CategoryProfile/components/CategoryProfileSubtitle/CategoryProfileSubtitle";
import {
    LAUNCHES_GROUPING_STORAGE_KEY,
    LAUNCHES_GROUPING_TABS,
    LAUNCHES_SEASON_LABELS,
    LAUNCHES_SEASON_ORDER,
    LAUNCHES_SEASON_STORAGE_KEY,
    LaunchesGrouping,
} from "../../Launches.constants";
import {
    buildMonthChart,
    buildSeasonChart,
    getCurrentSeason,
} from "../../Launches.helpers";
import "./LaunchesActivityChart.styles.scss";

const MIN_BAR_HEIGHT_PERCENT = 3;
const MAX_BAR_FILL_RATIO = 0.9;

interface Props {
    activity: CategoryProfileLaunches["activity"];
    categoryName: string;
}

const LaunchesActivityChart: FCX<Props> = ({ activity, categoryName, className }) => {
    const [grouping, setGrouping] = useLocalStorageState<LaunchesGrouping>(
        LAUNCHES_GROUPING_STORAGE_KEY,
        "season",
    );
    const [selectedSeason, setSelectedSeason] = useLocalStorageState(
        LAUNCHES_SEASON_STORAGE_KEY,
        getCurrentSeason(),
    );

    const seasonChart = useMemo(() => buildSeasonChart(activity.bySeason), [activity.bySeason]);
    const monthChart = useMemo(() => buildMonthChart(activity.byMonth), [activity.byMonth]);
    const cells = grouping === "season" ? seasonChart : monthChart;
    const isSeason = grouping === "season";

    const maxValue = useMemo(
        () => cells.flatMap(cell => cell.items).reduce((max, bar) => Math.max(max, bar.value), 0),
        [cells],
    );

    const getBarHeightPercent = useCallback(
        (value: number) => {
            const percent = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return Math.max(percent, MIN_BAR_HEIGHT_PERCENT) * MAX_BAR_FILL_RATIO;
        },
        [maxValue],
    );

    return (
        <div className={cn("LaunchesActivityChart", className)}>
            <div className="LaunchesActivityChart__header">
                <CategoryProfileSubtitle>
                    {`total US chain item launches: ${categoryName}`}
                </CategoryProfileSubtitle>
                <Tabs
                    data={LAUNCHES_GROUPING_TABS}
                    activeTabId={grouping}
                    onItemClick={(id) => setGrouping(id as LaunchesGrouping)}
                />
            </div>

            <div className="LaunchesActivityChart__content">
                <div className="LaunchesActivityChart__seasons">
                    {isSeason && LAUNCHES_SEASON_ORDER.map(season => (
                        <InputRadio
                            key={season}
                            label={LAUNCHES_SEASON_LABELS[season]}
                            isActive={season === selectedSeason}
                            onChange={() => setSelectedSeason(season)}
                        />
                    ))}
                </div>

                <div className="LaunchesActivityChart__chart">
                    {cells.map(cell => (
                        <div
                            key={cell.year}
                            className="LaunchesActivityChart__cell"
                            style={{ flexGrow: cell.items.length }}
                        >
                            <div className="LaunchesActivityChart__bars">
                                {cell.items.map(bar => {
                                    const isActiveSeason = bar.season === selectedSeason;
                                    const tooltipId = `LaunchesActivityChart-${bar.id}`;
                                    return (
                                        <div
                                            key={bar.id}
                                            className={cn("LaunchesActivityChart__bar", {
                                                "is-dimmed": isSeason && !isActiveSeason,
                                            })}
                                            style={{ height: `${getBarHeightPercent(bar.value)}%` }}
                                            data-tooltip-id={isSeason ? undefined : tooltipId}
                                        >
                                            {isSeason ? (
                                                <div className="LaunchesActivityChart__bar-value">
                                                    {bar.value}
                                                </div>
                                            ) : (
                                                <Tooltip
                                                    id={tooltipId}
                                                    place="top"
                                                    style={{ zIndex: 3 }}
                                                >
                                                    {`${bar.month} ${cell.year}: ${bar.value}`}
                                                </Tooltip>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="LaunchesActivityChart__year">{cell.year}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default LaunchesActivityChart;
